import * as angular from 'angular';

angular
  .module('bringoz.admin.commons')
  .directive('bringozDialogDraggable', dialogDraggable);

/**
 * Lets the user move an md-dialog by its toolbar and, unless
 * `bringoz-dialog-resizable="false"` is set, resize it from the bottom-right corner.
 *
 *   <md-dialog bringoz-dialog-draggable bringoz-dialog-resizable> ... </md-dialog>
 *
 * The drag handle defaults to the dialog's md-toolbar; mark a different element with
 * `bringoz-dialog-drag-handle` to override it.
 *
 * For a resizable dialog to be useful its body must follow the dialog height rather
 * than declare a fixed one - see planning-kpi-dialog.html for an example.
 */

const DRAG_HANDLE_SELECTOR = '[bringoz-dialog-drag-handle]';
const FALLBACK_DRAG_HANDLE_SELECTOR = 'md-toolbar';

// Clicking any of these inside the drag handle should do its own thing, not start a drag.
const INTERACTIVE_SELECTOR = [
  'button', 'a', 'input', 'textarea', 'select',
  'md-select', 'md-checkbox', 'md-switch', 'md-slider',
  'bringoz-button', 'bringoz-icon', '[ng-click]'
].join(', ');

// Floors chosen so a dialog shrunk all the way down is still usable: a toolbar, an
// actions bar and a section header eat ~180px of height before any body shows.
const MIN_WIDTH = 480;
const MIN_HEIGHT = 320;
const VIEWPORT_MARGIN = 8; // breathing room left around a dialog resized to the max
const KEEP_VISIBLE = 56;   // px of the dialog that must stay on screen when dragged

// One grab area per edge and corner, as a desktop window has. `x`/`y` say which edge
// the handle moves: -1 the left/top edge, 1 the right/bottom edge, 0 neither. The
// opposite edge stays anchored, so dragging the west handle only moves the left edge.
const RESIZE_HANDLES = [
  { name: 'n', x: 0, y: -1 },
  { name: 's', x: 0, y: 1 },
  { name: 'w', x: -1, y: 0 },
  { name: 'e', x: 1, y: 0 },
  { name: 'nw', x: -1, y: -1 },
  { name: 'ne', x: 1, y: -1 },
  { name: 'sw', x: -1, y: 1 },
  { name: 'se', x: 1, y: 1 }
];

function dialogDraggable($document, $window) {
  return {
    restrict: 'A',
    link: (scope, elem, attr) => {
      const dialog = elem[0];
      const doc = $document[0];

      // Offset from the dialog's resting position (md-dialog-container flex-centres it).
      let offsetX = 0;
      let offsetY = 0;
      let gesture: any = null;

      elem.addClass('br-draggable-dialog');

      const dragHandle = dialog.querySelector(DRAG_HANDLE_SELECTOR)
        || dialog.querySelector(FALLBACK_DRAG_HANDLE_SELECTOR);

      if (dragHandle) {
        dragHandle.classList.add('br-dialog-drag-handle');
        dragHandle.addEventListener('pointerdown', onDragStart);
      }

      const resizeHandles = [];

      if (angular.isDefined(attr.bringozDialogResizable) && attr.bringozDialogResizable !== 'false') {
        elem.addClass('br-resizable-dialog');

        RESIZE_HANDLES.forEach((direction) => {
          const handle = doc.createElement('div');
          handle.className = 'br-dialog-resize-handle br-dialog-resize-' + direction.name;
          handle.setAttribute('aria-hidden', 'true');

          const onPointerDown = (event) => onResizeStart(event, direction);

          handle.addEventListener('pointerdown', onPointerDown);
          dialog.appendChild(handle);
          resizeHandles.push({ element: handle, onPointerDown: onPointerDown });
        });
      }

      function onDragStart(event) {
        if (event.button !== 0) {
          return;
        }
        // Keep the close button (and anything else clickable in the toolbar) working.
        if (event.target.closest && event.target.closest(INTERACTIVE_SELECTOR)) {
          return;
        }

        const rect = dialog.getBoundingClientRect();

        gesture = {
          type: 'drag',
          pointerX: event.clientX,
          pointerY: event.clientY,
          offsetX: offsetX,
          offsetY: offsetY,
          width: rect.width,
          height: rect.height,
          // Where the dialog would sit with a zero offset.
          restLeft: rect.left - offsetX,
          restTop: rect.top - offsetY
        };

        beginGesture(event);
      }

      function onResizeStart(event, direction) {
        if (event.button !== 0) {
          return;
        }

        const rect = dialog.getBoundingClientRect();

        gesture = {
          type: 'resize',
          directionX: direction.x,
          directionY: direction.y,
          pointerX: event.clientX,
          pointerY: event.clientY,
          offsetX: offsetX,
          offsetY: offsetY,
          width: rect.width,
          height: rect.height
        };

        beginGesture(event);

        // Pin the size the dialog currently has BEFORE releasing the constraints below.
        // Skipping this leaves `width: auto` in force until the first pointermove, and
        // with flex-basis and max-width gone the dialog sizes itself to the table's
        // max-content width for a frame - or permanently, if the handle is clicked
        // without being moved.
        dialog.style.width = rect.width + 'px';
        dialog.style.height = rect.height + 'px';

        // md-dialog is sized by flex-basis (flex="80") and capped by max-width/max-height.
        // Both have to give way before an explicit width/height takes effect.
        dialog.style.flex = '0 0 auto';
        dialog.style.maxWidth = 'none';
        dialog.style.maxHeight = 'none';
      }

      function beginGesture(event) {
        event.preventDefault();
        elem.addClass('br-dialog-interacting');
        doc.addEventListener('pointermove', onPointerMove);
        doc.addEventListener('pointerup', endGesture);
        doc.addEventListener('pointercancel', endGesture);
      }

      function onPointerMove(event) {
        if (!gesture) {
          return;
        }

        const dx = event.clientX - gesture.pointerX;
        const dy = event.clientY - gesture.pointerY;

        if (gesture.type === 'drag') {
          moveTo(gesture.offsetX + dx, gesture.offsetY + dy);
        } else {
          resizeBy(dx, dy);
        }
      }

      function moveTo(x, y) {
        const viewportWidth = $window.innerWidth;
        const viewportHeight = $window.innerHeight;

        offsetX = clamp(
          x,
          KEEP_VISIBLE - gesture.restLeft - gesture.width,
          viewportWidth - KEEP_VISIBLE - gesture.restLeft
        );
        // Never allow the dialog above the viewport - the drag handle must stay grabbable.
        offsetY = clamp(
          y,
          -gesture.restTop,
          viewportHeight - KEEP_VISIBLE - gesture.restTop
        );

        applyOffset();
      }

      function resizeBy(dx, dy) {
        // Dragging a right/bottom edge (direction 1) grows the dialog as the pointer
        // moves positive; a left/top edge (-1) grows it as the pointer moves negative.
        // A direction of 0 means this handle does not touch that axis at all.
        const newWidth = gesture.directionX
          ? clamp(gesture.width + (gesture.directionX * dx), MIN_WIDTH, $window.innerWidth - (2 * VIEWPORT_MARGIN))
          : gesture.width;
        const newHeight = gesture.directionY
          ? clamp(gesture.height + (gesture.directionY * dy), MIN_HEIGHT, $window.innerHeight - (2 * VIEWPORT_MARGIN))
          : gesture.height;

        dialog.style.width = newWidth + 'px';
        dialog.style.height = newHeight + 'px';

        // The dialog is flex-centred, so a size change pushes both edges outwards by
        // half the delta. Shifting by half in the drag direction anchors the edge the
        // user is NOT dragging, so only the grabbed edge moves and it tracks the
        // pointer 1:1. Uses the clamped size, so the anchor holds at the limits too.
        offsetX = gesture.offsetX + (gesture.directionX * (newWidth - gesture.width) / 2);
        offsetY = gesture.offsetY + (gesture.directionY * (newHeight - gesture.height) / 2);

        applyOffset();
      }

      function applyOffset() {
        // md-dialog is `position: relative`, so left/top nudge it without disturbing
        // the container's centring or the open/close transforms.
        dialog.style.left = offsetX + 'px';
        dialog.style.top = offsetY + 'px';
      }

      function endGesture() {
        gesture = null;
        elem.removeClass('br-dialog-interacting');
        doc.removeEventListener('pointermove', onPointerMove);
        doc.removeEventListener('pointerup', endGesture);
        doc.removeEventListener('pointercancel', endGesture);
      }

      scope.$on('$destroy', () => {
        endGesture();
        if (dragHandle) {
          dragHandle.removeEventListener('pointerdown', onDragStart);
        }
        resizeHandles.forEach((handle) => {
          handle.element.removeEventListener('pointerdown', handle.onPointerDown);
        });
      });
    }
  };
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}
